import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { LogIn, KeyRound } from 'lucide-react';
import {
  accountSignIn,
  accountForgotPassword,
  accountConfirmForgotPassword,
} from '../accountAuth';
import { Button, Card, Input, Alert } from './ui';

type Mode = 'login' | 'forgot' | 'reset';

const AccountLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedNext = searchParams.get('next');
  const next = requestedNext?.startsWith('/') && !requestedNext.startsWith('//') ? requestedNext : null;
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await accountSignIn(email, password);
      navigate(next || '/my-codes');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Login failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await accountForgotPassword(email);
      setInfo('Reset code sent. Check your email.');
      setMode('reset');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to send reset code';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await accountConfirmForgotPassword(email, resetCode, newPassword);
      setInfo('Password reset! You can now log in.');
      setMode('login');
      setPassword('');
      setResetCode('');
      setNewPassword('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Reset failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Log In - YouWont</title>
      </Helmet>
      <div className="min-h-screen py-10 px-4 sm:px-6 lg:px-8 flex items-start justify-center">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-brand/10 text-brand dark:bg-brand/20 dark:text-blue-400 mb-4">
              {mode === 'login' ? <LogIn size={28} /> : <KeyRound size={28} />}
            </div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-2">
              {mode === 'login' ? 'Welcome Back' : 'Forgot Password?'}
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              {mode === 'login'
                ? 'Your codes missed you. Probably.'
                : mode === 'forgot'
                ? "Happens to the best of us. We'll sort it out."
                : "Check your inbox, grab that code, and let's get you back in."}
            </p>
          </div>

          <Card className="brand-panel animate-fade-in">
            {mode === 'login' && (
              <form onSubmit={handleLogin} className="space-y-4">
                <Input
                  label="Email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <Input
                  label="Password"
                  type="password"
                  placeholder="Your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />

                {info && <Alert variant="success">{info}</Alert>}
                {error && <Alert variant="danger">{error}</Alert>}

                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => { setError(null); setInfo(null); setMode('forgot'); }}
                    className="text-sm text-brand hover:text-brand-600 underline dark:text-blue-400"
                  >
                    Forgot password?
                  </button>
                </div>

                <Button type="submit" variant="primary" disabled={loading} className="w-full">
                  {loading ? 'Logging In...' : 'Log In'}
                </Button>

                <p className="text-center text-sm text-slate-500 dark:text-slate-400">
                  Don&apos;t have an account?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/account/signup')}
                    className="text-brand hover:text-brand-600 underline dark:text-blue-400"
                  >
                    Sign up
                  </button>
                </p>
              </form>
            )}

            {mode === 'forgot' && (
              <form onSubmit={handleForgot} className="space-y-4">
                <Input
                  label="Email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />

                {error && <Alert variant="danger">{error}</Alert>}

                <Button type="submit" variant="primary" disabled={loading} className="w-full">
                  {loading ? 'Sending...' : 'Send Reset Code'}
                </Button>

                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => { setError(null); setMode('login'); }}
                  className="w-full"
                >
                  Back to Login
                </Button>
              </form>
            )}

            {mode === 'reset' && (
              <form onSubmit={handleReset} className="space-y-4">
                {info && <Alert variant="info">{info}</Alert>}

                <Input
                  label="Reset Code"
                  type="text"
                  placeholder="6-digit code from email"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                />
                <Input
                  label="New Password"
                  type="password"
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  hint="Min 8 characters with uppercase, lowercase, number, and symbol"
                  required
                />

                {error && <Alert variant="danger">{error}</Alert>}

                <Button type="submit" variant="primary" disabled={loading} className="w-full">
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>

                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => { setError(null); setInfo(null); setMode('login'); }}
                  className="w-full"
                >
                  Back to Login
                </Button>
              </form>
            )}
          </Card>
        </div>
      </div>
    </>
  );
};

export default AccountLogin;
